
    import {Inject, Options, Vue} from 'vue-property-decorator';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/components/for-creation/store/getters';
    import type {ProfileCreationForm} from '@/profile-creation-wizard/components/for-creation/store';
    import CategorizationManualSelection from '@/common/components/categorization/CategorizationManualSelection.vue';
    import CategorizationPrediction from '@/common/components/categorization/CategorizationPrediction.vue';
    import CategorizationLevelSelection from '@/profile-creation-wizard/components/for-creation/ui/2_categorization/CategorizationLevelSelection.vue';
    import CategorizationSide from '@/profile-creation-wizard/components/for-creation/ui/2_categorization/CategorizationSide.vue';
    import CategorizationDetailsSkeleton from '@/common/components/categorization/CategorizationDetailsSkeleton.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {CategorizationRepository} from '@/common/repositories/CategorizationRepository';
    import {PredictCategoryResource, ProfileFamily} from '@/common/api/api-raw-types';
    import mutations from '@/profile-creation-wizard/components/for-creation/store/mutations';
    import {CreationFormValidation} from '@/profile-creation-wizard/components/for-creation/ProfileCreationForm';
    import StepActions from '@/profile-creation-wizard/common/ui/step/StepActions.vue';
    import StepMobileHeader from '@/profile-creation-wizard/common/ui/step/StepMobileHeader.vue';
    import {TrackingRepository} from '@/common/repositories/track/TrackingRepository';
    import {StepNavigator} from '@/profile-creation-wizard/common/step/StepNavigator';

    @Options({
        components: {
            CategorizationSide,
            CategorizationDetailsSkeleton,
            CategorizationManualSelection,
            CategorizationLevelSelection,
            CategorizationPrediction,
            StepActions,
            StepMobileHeader,
        },
    })
    export default class CategorizationStep extends Vue {
        @Inject({from: DI.categorizationRepository})
        categorizationRepository!: CategorizationRepository;
        @Inject({from: DI.creationFormValidation})
        creationFormValidation!: CreationFormValidation;
        @Inject({from: DI.trackingRepository})
        trackingRepository!: TrackingRepository;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        families: ProfileFamily[] = [];
        progressPercentage = 0;
        predictions: PredictCategoryResource[] = [];
        predictionsKeeper: PredictCategoryResource[] = [];
        hadPredictions = false;
        isLoadingPredictions = true;

        stepName = 'categorization';

        async created(): Promise<void> {
            await this.trackingRepository.track('categorization-view-page-v2');

            const meta = this.$router.currentRoute.value.meta;
            this.progressPercentage = meta.progressPercentage || 0;
            this.stepNavigator = new StepNavigator(this.$router, meta.previousStep, meta.nextStep);

            await this.getPredictions();
            await this.loadFamilies();
        }

        async getPredictions(): Promise<void> {
            if (this.predictionsKeeper.length > 0) {
                this.predictions = [...this.predictionsKeeper];
                return;
            }

            this.isLoadingPredictions = true;
            const [predictions, error] = await this.categorizationRepository.predict(this.creationForm.headline!);

            if (predictions && !error) {
                this.predictions = predictions;
                this.hadPredictions = this.predictions.length > 0;
            }

            this.isLoadingPredictions = false;
        }

        async loadFamilies(): Promise<void> {
            const [families, error] = await this.categorizationRepository.getAll();

            if (families && !error) {
                this.families = families;
                this.$store.commit(mutations.loadFamiliesDefinition, families);
            }
        }

        async switchToManualSelection(): Promise<void> {
            if (this.families.length === 0) {
                await this.loadFamilies();
            }
            this.predictionsKeeper = [...this.predictions];
            this.predictions = [];
        }

        get hasPredictions(): boolean {
            return this.predictions.length > 0;
        }

        manualSelection(): void {
            this.$store.commit(mutations.switchToCategoryManual);
        }

        predictionSelection(): void {
            this.$store.commit(mutations.switchToCategoryPrediction);
        }

        manualSelectFamily(familyId: string): void {
            this.$store.commit(mutations.selectManualFamily, familyId);
        }

        manualSelectCategory(categories: string[]): void {
            this.$store.commit(mutations.selectManualCategories, categories);
        }

        manualSelectOtherCategory(otherCategory: string): void {
            this.$store.commit(mutations.selectManualSuggestedCategory, otherCategory);
        }

        manualUnselectCategories(): void {
            this.$store.commit(mutations.unselectManualCategories);
        }

        selectFamilyAndCategory(familyAndCategory: any): void {
            const action = familyAndCategory.action;
            const selection = {
                family: familyAndCategory.familyId,
                category: familyAndCategory.categoryId,
            };
            if (action === 'unselect') {
                this.$store.commit(mutations.unselectPredictionFamilyAndCategory, selection);
            } else {
                this.$store.commit(mutations.selectPredictionFamilyAndCategory, selection);
            }
        }

        get hasValidFieldPart(): boolean {
            const form = this.creationForm;
            return (
                this.creationForm.familiesCategoriesEdition.hasCompleteSelection() &&
                this.creationFormValidation.isExperienceLevelValid(form.experienceLevel)
            );
        }

        goNext(): void {
            const familiesAndCategories = this.creationForm.familiesCategoriesEdition.toRequest();
            this.trackingRepository.track('categorization-chosen-v2', {
                families: familiesAndCategories.families,
                categories: familiesAndCategories.categories,
                suggestionCategory: familiesAndCategories.categorySuggestion,
                fromCategoryPredictor: this.predictions.length > 0,
            });

            this.trackingRepository.track('expertise-level-chosen-v2', {
                expertiseLevel: this.creationForm.experienceLevel,
            });

            this.stepNavigator.next();
        }

        goToPrevious(): void {
            this.stepNavigator.previous();
        }

        get isLoading(): boolean {
            return this.isLoadingPredictions;
        }
    }
